export interface QuizOption {
  id: string;
  label: string;
  icon: string;
  value: string;
}

export interface QuizQuestion {
  id: string;
  question: string;
  subtitle: string;
  options: QuizOption[];
}

export interface QuizResult {
  id: string;
  title: string;
  subtitle: string;
  desc: string;
  traits: string[];
  productCategory: "dzi" | "crystal" | "jade" | "thangka";
  productIds: string[];
  img: string;
  color: string;
  bgColor: string;
  accent: string;
}

export const quizQuestions: QuizQuestion[] = [
  {
    id: "q1",
    question: "你目前最渴望的是什麼？",
    subtitle: "選擇最貼近你內心的答案",
    options: [
      { id: "q1a", label: "財富與事業順遂", icon: "ri-money-dollar-circle-line", value: "wealth" },
      { id: "q1b", label: "內心平靜與療癒", icon: "ri-heart-pulse-line", value: "peace" },
      { id: "q1c", label: "愛情與人際和諧", icon: "ri-heart-3-line", value: "love" },
      { id: "q1d", label: "智慧與靈性成長", icon: "ri-lightbulb-flash-line", value: "wisdom" },
    ],
  },
  {
    id: "q2",
    question: "你的生活步調是？",
    subtitle: "誠實回答，沒有對錯",
    options: [
      { id: "q2a", label: "積極進取，目標明確", icon: "ri-rocket-line", value: "active" },
      { id: "q2b", label: "沉穩內斂，重視感受", icon: "ri-leaf-line", value: "calm" },
      { id: "q2c", label: "溫暖開朗，重視關係", icon: "ri-sun-line", value: "warm" },
      { id: "q2d", label: "深思熟慮，追求意義", icon: "ri-moon-line", value: "deep" },
    ],
  },
  {
    id: "q3",
    question: "你最喜歡哪種能量氛圍？",
    subtitle: "跟著直覺選擇",
    options: [
      { id: "q3a", label: "神秘古老的西藏文化", icon: "ri-ancient-gate-line", value: "tibet" },
      { id: "q3b", label: "大自然的礦石能量", icon: "ri-sparkling-2-line", value: "nature" },
      { id: "q3c", label: "東方玉石的溫潤氣質", icon: "ri-leaf-line", value: "jade" },
      { id: "q3d", label: "佛法信仰的守護力量", icon: "ri-shield-star-line", value: "buddha" },
    ],
  },
  {
    id: "q4",
    question: "你希望飾品帶給你什麼感覺？",
    subtitle: "選擇最能打動你的描述",
    options: [
      { id: "q4a", label: "強大的守護與安全感", icon: "ri-shield-check-line", value: "protect" },
      { id: "q4b", label: "清澈的思緒與專注力", icon: "ri-focus-3-line", value: "focus" },
      { id: "q4c", label: "溫柔的療癒與放鬆", icon: "ri-seedling-line", value: "heal" },
      { id: "q4d", label: "吉祥的祝福與好運", icon: "ri-star-smile-line", value: "luck" },
    ],
  },
  {
    id: "q5",
    question: "你通常在哪個時刻最需要能量支持？",
    subtitle: "選擇最符合你的情境",
    options: [
      { id: "q5a", label: "工作壓力大、需要突破", icon: "ri-briefcase-4-line", value: "work" },
      { id: "q5b", label: "情緒低落、需要療癒", icon: "ri-emotion-sad-line", value: "emotion" },
      { id: "q5c", label: "人際關係、需要和諧", icon: "ri-group-line", value: "relation" },
      { id: "q5d", label: "靈性修行、需要加持", icon: "ri-mental-health-line", value: "spirit" },
    ],
  },
];

export const quizResults: QuizResult[] = [
  {
    id: "dzi",
    title: "西藏天珠",
    subtitle: "千年守護的能量之珠",
    desc: "你擁有強烈的目標感與行動力，渴望在事業與財富上有所突破。西藏天珠承載著千年的藏傳文化能量，是最強大的守護飾品，能為你帶來貴人相助、財富流入與事業順遂的強大能量加持。",
    traits: ["目標明確", "積極進取", "渴望突破", "重視守護"],
    productCategory: "dzi",
    productIds: ["dzi-001", "dzi-002", "dzi-005"],
    img: "https://readdy.ai/api/search-image?query=nine%20eye%20Tibetan%20dzi%20bead%20bracelet%20collection%20on%20dark%20stone%20surface%20warm%20golden%20atmospheric%20light%20mystical%20ancient%20energy%20jewelry%20photography%20elegant&width=800&height=500&seq=quiz_dzi_result&orientation=landscape",
    color: "#8b6f47",
    bgColor: "#fdf8f2",
    accent: "#f5ede0",
  },
  {
    id: "crystal",
    title: "天然水晶礦石",
    subtitle: "大地孕育的療癒能量",
    desc: "你內心細膩敏感，重視情感與靈性的平衡。天然水晶礦石由大地億萬年孕育而成，每一顆都承載著獨特的能量頻率，能夠幫助你淨化負能量、提升直覺力，帶來內心的平靜與療癒。",
    traits: ["情感豐富", "直覺敏銳", "重視療癒", "追求平衡"],
    productCategory: "crystal",
    productIds: ["crystal-001", "crystal-002", "crystal-005"],
    img: "https://readdy.ai/api/search-image?query=collection%20of%20natural%20crystal%20gemstone%20bracelets%20amethyst%20rose%20quartz%20on%20white%20marble%20surface%20soft%20natural%20light%20healing%20energy%20jewelry%20photography%20elegant&width=800&height=500&seq=quiz_crystal_result&orientation=landscape",
    color: "#7a6a9a",
    bgColor: "#f8f6fc",
    accent: "#f0edf7",
  },
  {
    id: "jade",
    title: "翡翠玉石",
    subtitle: "東方文化的吉祥象徵",
    desc: "你溫潤內斂，重視家庭與人際關係的和諧，對東方文化有深厚的情感連結。翡翠玉石是中華文化中最珍貴的吉祥之物，溫潤的質感象徵君子之德，能為你帶來平安吉祥、萬事如意的美好祝福。",
    traits: ["溫潤典雅", "重視家庭", "文化底蘊", "追求和諧"],
    productCategory: "jade",
    productIds: ["jade-001", "jade-002", "jade-003"],
    img: "https://readdy.ai/api/search-image?query=jade%20jadeite%20green%20jewelry%20collection%20pendant%20bracelet%20on%20white%20marble%20surface%20soft%20natural%20light%20oriental%20elegant%20cultural%20heritage%20jewelry%20photography&width=800&height=500&seq=quiz_jade_result&orientation=landscape",
    color: "#4a7a52",
    bgColor: "#f4f9f5",
    accent: "#eaf2eb",
  },
  {
    id: "thangka",
    title: "唐卡佛牌",
    subtitle: "信仰與藝術的守護力量",
    desc: "你有深厚的靈性追求，重視信仰的力量與心靈的歸依。唐卡佛牌融合了藏傳佛教的智慧與精湛的藝術工藝，每一件都是修行者的心血結晶，能為你帶來佛法的加持、消災解厄與智慧圓滿的守護。",
    traits: ["靈性深厚", "重視信仰", "追求智慧", "心靈歸依"],
    productCategory: "thangka",
    productIds: ["thangka-001", "thangka-003", "thangka-005"],
    img: "https://readdy.ai/api/search-image?query=Tibetan%20thangka%20painting%20Buddhist%20art%20collection%20colorful%20traditional%20artwork%20on%20dark%20background%20warm%20golden%20atmospheric%20light%20cultural%20heritage%20spiritual%20collectible%20photography&width=800&height=500&seq=quiz_thangka_result&orientation=landscape",
    color: "#c9a96e",
    bgColor: "#fdf9f2",
    accent: "#faf5ec",
  },
];

// Scoring logic: maps answer values to result categories
export const scoreMap: Record<string, Record<string, number>> = {
  // Q1 desires
  wealth:  { dzi: 3, crystal: 0, jade: 1, thangka: 1 },
  peace:   { dzi: 0, crystal: 3, jade: 1, thangka: 1 },
  love:    { dzi: 0, crystal: 2, jade: 3, thangka: 0 },
  wisdom:  { dzi: 1, crystal: 1, jade: 0, thangka: 3 },
  // Q2 pace
  active:  { dzi: 3, crystal: 0, jade: 1, thangka: 1 },
  calm:    { dzi: 0, crystal: 2, jade: 3, thangka: 1 },
  warm:    { dzi: 1, crystal: 1, jade: 3, thangka: 0 },
  deep:    { dzi: 0, crystal: 1, jade: 0, thangka: 3 },
  // Q3 vibe
  tibet:   { dzi: 4, crystal: 0, jade: 0, thangka: 1 },
  nature:  { dzi: 0, crystal: 4, jade: 1, thangka: 0 },
  jade:    { dzi: 0, crystal: 0, jade: 4, thangka: 1 },
  buddha:  { dzi: 1, crystal: 0, jade: 0, thangka: 4 },
  // Q4 feeling
  protect: { dzi: 3, crystal: 1, jade: 1, thangka: 2 },
  focus:   { dzi: 1, crystal: 3, jade: 0, thangka: 2 },
  heal:    { dzi: 0, crystal: 3, jade: 2, thangka: 1 },
  luck:    { dzi: 2, crystal: 0, jade: 3, thangka: 2 },
  // Q5 moment
  work:    { dzi: 3, crystal: 1, jade: 1, thangka: 1 },
  emotion: { dzi: 0, crystal: 3, jade: 1, thangka: 2 },
  relation:{ dzi: 0, crystal: 1, jade: 3, thangka: 1 },
  spirit:  { dzi: 1, crystal: 1, jade: 0, thangka: 4 },
};
